import type {Suggestion, Suggestions} from '@github-ui/inline-autocomplete/types'
import {useQuery} from '@github-ui/react-query'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {useMemo} from 'react'

import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../../utils/reference-mention'
import {DiscussionSuggestion} from './DiscussionSuggestion'
import {createEmptyStateSuggestion} from './EmptyStateSuggestion'
import {useDebouncedFilterQuery} from './use-debounced-filter-query'

export interface DiscussionsQuery {
  category: 'discussions'
  filter: string
  repository: string
}

export function useDiscussionsSuggestions(query: DiscussionsQuery | null) {
  const {service} = useChatManager()

  const debouncedQuery = useDebouncedFilterQuery(query)
  const isDebouncing = query !== debouncedQuery

  const discussionsQuery = useQuery({
    enabled: debouncedQuery !== null,
    queryKey: ['copilot-autocomplete-discussions', debouncedQuery?.repository, debouncedQuery?.filter],
    queryFn: async () => service.fetchAutocompleteDiscussions(debouncedQuery!.repository, debouncedQuery!.filter),
    placeholderData: prev => prev,
  })

  const suggestions = useMemo<Suggestions | null>(() => {
    if (!debouncedQuery) return null

    if (discussionsQuery.isLoading) return 'loading'

    const items =
      discussionsQuery.data?.map<Suggestion>(discussion => ({
        value: ReferenceMention.stringify({
          type: 'discussion',
          repo: debouncedQuery.repository,
          id: discussion.number.toString(),
        }),
        key: discussion.number.toString(),
        render: props => (
          <DiscussionSuggestion
            {...props}
            discussion={discussion}
            stale={discussionsQuery.isPlaceholderData || isDebouncing}
          />
        ),
      })) ?? null

    if (items && items.length === 0 && !discussionsQuery.isPlaceholderData && !isDebouncing) {
      return [createEmptyStateSuggestion('discussions', <CommentDiscussionIcon />, 'No discussions found')]
    }

    return items
  }, [
    discussionsQuery.data,
    discussionsQuery.isLoading,
    discussionsQuery.isPlaceholderData,
    debouncedQuery,
    isDebouncing,
  ])

  return {suggestions, stale: discussionsQuery.isPlaceholderData}
}
